// src/pages/GaleriesLanding.jsx
// Page publique /galeries : offre ARTICHAUT pour les galeristes (CPGA), sur le patrimoine propre de la galerie,
// ses archives et les ensembles liés aux artistes. Même gabarit que /notaires.
// Formulaire → fonction contact-lead (table professional_leads, accusé de réception, notification équipe).
// Règles : ne pas remplacer Artlogic ni le logiciel de la galerie ; aucune promesse d'intégration ; aucune
// question sur la valeur de la collection ; pas de gestion juridique complète d'un estate.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const USAGES = [
  {
    n: "Parcours 1",
    t: "La collection propre du galeriste",
    d: "Pour les œuvres acquises ou conservées à titre personnel, qui doivent être distinguées du stock commercial et préparées pour une transmission privée.",
  },
  {
    n: "Parcours 2",
    t: "Le patrimoine et les archives de la galerie",
    d: "Pour documenter l'histoire de la galerie, ses expositions, ses archives, ses œuvres historiques et les informations nécessaires à une cession, une fermeture ou une succession.",
  },
  {
    n: "Parcours 3",
    t: "L'accompagnement d'un artist estate",
    d: "Pour réunir les œuvres, documents, provenances, expositions, localisations, interlocuteurs et informations utiles aux héritiers ou ayants droit.",
  },
];

const STEPS = [
  "Identifier les ensembles et leurs propriétaires.",
  "Rassembler les œuvres, documents et provenances.",
  "Signaler les informations manquantes ou incertaines.",
  "Organiser les accès des personnes concernées.",
  "Produire un dossier de continuité ou de transmission.",
];

const PRIVACY = [
  ["Qui peut consulter", "Uniquement les personnes que vous désignez, pour les ensembles que vous choisissez."],
  ["Comment les accès sont accordés et retirés", "Pour une durée limitée si vous le souhaitez, révocables à tout moment ; les consultations par lien partagé sont journalisées."],
  ["Comment les données sont exportées", "La galerie peut exporter la liste de ses œuvres et leurs images à tout moment. L'export des documents, de l'historique et des droits d'accès est en préparation."],
  ["Ce qu'ARTICHAUT ne conserve pas", "Aucun mot de passe, aucune clé privée, aucune phrase de récupération : seulement où se trouve un accès, qui le détient et la procédure à suivre."],
];

const NEEDS = [
  ["own", "Collection propre"],
  ["archives", "Archives"],
  ["gallery", "Transmission de la galerie"],
  ["estate", "Artist estate"],
  ["other", "Autre"],
];

const EMPTY = {
  gallery: "", contact: "", role: "", email: "", phone: "", need: "own", volume: "", tool: "", deadline: "",
  callback: false, consent: false, website: "",
};

const input = "px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4] w-full";

export default function GaleriesLanding() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const contactRef = useRef(null);

  useEffect(() => {
    document.title = `${BRAND_NAME} pour les galeries — la galerie se transmet, sa mémoire aussi`;
    track("galeries_page_view", { meta: { source: new URLSearchParams(window.location.search).get("utm_source") || document.referrer || null } });
  }, []);

  const goContact = () => {
    requestAnimationFrame(() => contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gallery.trim()) { setError("Merci d'indiquer le nom de la galerie."); return; }
    if (!form.contact.trim()) { setError("Merci d'indiquer votre nom."); return; }
    if (!EMAIL_RE.test(form.email.trim())) { setError("Merci d'indiquer une adresse e-mail valide."); return; }
    if (!form.consent) { setError("Merci de cocher la case de consentement."); return; }
    setSending(true); setError(null);
    try {
      const [firstName, ...rest] = form.contact.trim().split(/\s+/);
      const needLabel = (NEEDS.find(([v]) => v === form.need) || [])[1] || form.need;
      // La fonction contact-lead stocke la demande dans professional_leads ; le préfixe [Galeries]
      // permet de les retrouver (source « contact-general »).
      const message = [
        "[Galeries]",
        `Besoin principal : ${needLabel}`,
        form.role.trim() && `Fonction : ${form.role.trim()}`,
        form.volume.trim() && `Volume approximatif : ${form.volume.trim()}`,
        form.tool.trim() && `Outil de gestion actuel : ${form.tool.trim()}`,
        form.deadline.trim() && `Échéance : ${form.deadline.trim()}`,
        `Rappel souhaité : ${form.callback ? "oui" : "non"}`,
      ].filter(Boolean).join("\n");

      const { data, error: err } = await supabase.functions.invoke("contact-lead", {
        body: {
          first_name: firstName || "",
          last_name: rest.join(" "),
          company: form.gallery.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          client_count: form.volume.trim(),
          message,
          website: form.website, // champ piège : doit rester vide
          brand: BRAND_NAME,
          anon_id: getAnonId(),
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("lead_failed");
      track("galeries_lead", { meta: { need: form.need, callback: form.callback } });
      setDone(true);
    } catch (err) {
      console.error("[galeries] lead:", err);
      setError("Un problème est survenu. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" aria-label={BRAND_NAME} className="no-underline text-inherit"><BrandMark className="text-2xl tracking-[0.15em]" /></Link>
          <div className="flex items-center gap-4">
            <a href="#usages" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Les usages</a>
            <a href="#methode" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">La méthode</a>
            <a href="#confidentialite" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Confidentialité</a>
            <button onClick={goContact} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              Pour ma galerie
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-14">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">Pour les galeries</div>
        <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-3xl">La galerie se transmet. Sa mémoire aussi.</h1>
        <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">
          {BRAND_NAME} aide les galeristes à documenter leur collection propre, leurs archives et les ensembles liés aux artistes, afin d'en préparer la continuité et la transmission.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={goContact} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
            Demander un diagnostic confidentiel
          </button>
          <a href="#usages" className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
            Découvrir les usages pour les galeries
          </a>
        </div>
      </section>

      {/* DIFFÉRENCIATION */}
      <section className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-3xl font-bold mb-4">Ni stock commercial, ni simple base d'œuvres</h2>
          <p className="text-lg text-[#4a453d] leading-relaxed max-w-3xl">
            Vos outils de gestion suivent l'activité quotidienne de la galerie. {BRAND_NAME} intervient sur ce qui doit rester intelligible lorsque l'activité se transforme ou s'arrête : la collection du galeriste, les archives, l'histoire des œuvres et les informations nécessaires aux successeurs, héritiers et ayants droit.
          </p>
        </div>
      </section>

      {/* TROIS USAGES */}
      <section id="usages" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">Trois usages</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {USAGES.map((u) => (
            <div key={u.n} className="bg-white border border-[#e5e0d8] rounded-2xl p-6">
              <div className="text-xs tracking-[0.16em] uppercase text-[#a9843f] mb-3">{u.n}</div>
              <h3 className="text-xl font-bold mb-3 leading-snug">{u.t}</h3>
              <p className="text-sm text-[#4a453d] leading-relaxed">{u.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm italic text-[#6b6459] max-w-3xl">
          {BRAND_NAME} constitue la couche documentaire et patrimoniale. Il ne se substitue ni à la gestion juridique d'un estate, ni à l'authentification, ni aux licences, ni au catalogue raisonné : il permet aux ayants droit, galeries, experts et professionnels du droit de travailler sur un dossier structuré.
        </p>
      </section>

      {/* MÉTHODE */}
      <section id="methode" className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold mb-8">La méthode</h2>
          <ol className="grid gap-3">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-start gap-4 bg-[#f9f7f4] border border-[#e5e0d8] rounded-xl px-5 py-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-[#1a1a1a] text-[#c9a961] flex items-center justify-center text-sm font-bold font-sans">{i + 1}</span>
                <span className="text-base text-[#2a261f] pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CONFIANCE */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-4">Pensé par une galeriste</h2>
        <p className="text-lg text-[#4a453d] leading-relaxed">
          {BRAND_NAME} a été fondé par Vanessa Quang, galeriste pendant dix-sept ans et ancienne membre du Comité professionnel des galeries d'art. Le service est né d'un constat de terrain : au-delà du stock et des ventes, une galerie accumule un patrimoine, des archives et une mémoire qu'il faut pouvoir préserver et transmettre.
        </p>
      </section>

      {/* COMPLÉMENTARITÉ */}
      <section className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <h2 className="text-3xl font-bold mb-4">En complément de vos outils existants</h2>
          <p className="text-lg text-[#4a453d] leading-relaxed">
            {BRAND_NAME} n'a pas vocation à remplacer Artlogic ni le logiciel de gestion de la galerie. Une importation structurée pourra être étudiée afin d'éviter les doubles saisies ; aucune intégration n'est promise avant validation technique.
          </p>
        </div>
      </section>

      {/* CONFIDENTIALITÉ */}
      <section id="confidentialite" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">Vous gardez la main sur votre dossier</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {PRIVACY.map(([t, d]) => (
            <div key={t} className="bg-white border border-[#e5e0d8] rounded-xl p-5">
              <h3 className="font-bold text-[#2a261f] mb-1">{t}</h3>
              <p className="text-sm text-[#4a453d] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FORMULAIRE */}
      <section ref={contactRef} id="contact" className="max-w-3xl mx-auto px-6 pb-16 pt-4">
        <h2 className="text-3xl font-bold mb-2">Que deviendra le patrimoine de votre galerie ?</h2>
        <p className="text-[#4a453d] mb-8">
          Nous vous proposons un premier échange confidentiel pour distinguer le stock, la collection propre, les archives et les ensembles à transmettre.
        </p>

        {done ? (
          <div className="bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-8 text-center">
            <div className="text-2xl mb-2">✓</div>
            <h3 className="text-lg font-bold mb-1">Demande enregistrée</h3>
            <p className="text-sm text-[#4a453d]">Merci. Vous allez recevoir un accusé de réception par e-mail (pensez à vérifier vos courriers indésirables). Vanessa Quang revient vers vous pour un premier échange confidentiel.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 md:p-8 grid gap-4">
            {/* Champ piège : invisible pour les personnes, rempli par les robots */}
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={field("website")}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <input value={form.gallery} onChange={field("gallery")} placeholder="Nom de la galerie *" className={input} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.contact} onChange={field("contact")} placeholder="Nom du contact *" className={input} />
              <input value={form.role} onChange={field("role")} placeholder="Fonction" className={input} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="email" value={form.email} onChange={field("email")} placeholder="Adresse électronique *" className={input} />
              <input type="tel" value={form.phone} onChange={field("phone")} placeholder="Téléphone (facultatif)" className={input} />
            </div>
            <div>
              <label className="block text-xs text-[#8a8378] mb-2">Besoin principal</label>
              <div className="flex flex-wrap gap-2">
                {NEEDS.map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setForm((f) => ({ ...f, need: v }))}
                    className={`px-4 py-2 rounded-lg text-sm border transition-colors ${form.need === v ? "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.volume} onChange={field("volume")} placeholder="Volume approximatif d'œuvres ou de documents" className={input} />
              <input value={form.tool} onChange={field("tool")} placeholder="Outil de gestion actuellement utilisé" className={input} />
            </div>
            <input value={form.deadline} onChange={field("deadline")} placeholder="Échéance éventuelle" className={input} />
            <label className="flex items-start gap-3 text-sm text-[#4a453d]">
              <input type="checkbox" checked={form.callback} onChange={(e) => setForm((f) => ({ ...f, callback: e.target.checked }))} className="mt-1" />
              <span>Je souhaite être rappelé(e).</span>
            </label>
            <label className="flex items-start gap-3 text-xs text-[#6b6459]">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5" />
              <span>J'accepte que ces informations servent à me répondre. Elles ne sont ni revendues ni utilisées pour un autre usage — voir la{" "}
                <Link to="/privacy-policy" className="underline">politique de confidentialité</Link>.</span>
            </label>
            {error && <p className="text-sm text-[#dc2626]">{error}</p>}
            <button type="submit" disabled={sending} className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-60">
              {sending ? "Envoi…" : "Échanger avec Vanessa Quang"}
            </button>
            <p className="text-xs text-[#8a8378]">
              Nous ne vous demandons ni la valeur de votre collection ni de donnée sensible lors de ce premier contact.
            </p>
          </form>
        )}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME} — par ARTKIV</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">Mentions légales</Link>
          <Link to="/privacy-policy" className="hover:text-black">Confidentialité</Link>
        </span>
      </footer>
    </div>
  );
}
